import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Gavel, Clock, Loader2, Trophy, AlertTriangle, DollarSign, ChevronRight, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const MyAuctions = ({ user }) => {
  const [activeTab, setActiveTab] = useState("bidding");
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reputation, setReputation] = useState(null);

  useEffect(() => {
    fetchAuctions();
    fetchReputation();
  }, [activeTab]);

  const fetchAuctions = async () => {
    setLoading(true);
    try {
      const role = activeTab === "selling" ? "seller" : "bidder";
      const response = await api.get(`/my/auctions?role=${role}`);
      setAuctions(response.data.auctions || []);
    } catch (error) {
      logger.error("Failed to fetch auctions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReputation = async () => {
    try {
      const response = await api.get("/my/reputation");
      setReputation(response.data);
    } catch (error) {
      logger.error("Failed to fetch reputation:", error);
    }
  };

  const formatTimeRemaining = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return "Ended";
    
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getStatusBadge = (auction) => {
    if (auction.status === "active") {
      return <span className="px-2 py-0.5 bg-[#22C55E]/10 text-[#22C55E] rounded text-xs">Live</span>;
    }
    if (["sold", "captured", "payment_protected"].includes(auction.status)) {
      return <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded text-xs">Payment Protected</span>;
    }
    if (auction.status === "delivery_reported") {
      return <span className="px-2 py-0.5 bg-[#3B82F6]/10 text-[#3B82F6] rounded text-xs">Buyer Review</span>;
    }
    if (auction.status === "disputed") {
      return <span className="px-2 py-0.5 bg-[#EF4444]/10 text-[#EF4444] rounded text-xs">Disputed</span>;
    }
    if (["completed", "released"].includes(auction.status)) {
      return <span className="px-2 py-0.5 bg-[#22C55E]/10 text-[#22C55E] rounded text-xs">Complete</span>;
    }
    if (auction.status === "refunded") {
      return <span className="px-2 py-0.5 bg-[#71717A]/10 text-[#A1A1AA] rounded text-xs">Refunded</span>;
    }
    if (auction.status === "ended") {
      return <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded text-xs">Awaiting Payment</span>;
    }
    if (auction.status === "no_sale") {
      return <span className="px-2 py-0.5 bg-[#71717A]/10 text-[#71717A] rounded text-xs">No Sale</span>;
    }
    return <span className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] rounded text-xs capitalize">{auction.status}</span>;
  };

  const ReputationCard = () => {
    if (!reputation) return null;
    
    const score = reputation.reputation?.score || 50;
    const tier = reputation.tier || "standard";
    const tierColors = {
      trusted: "text-[#22C55E] bg-[#22C55E]/10",
      standard: "text-[#D4AF37] bg-[#D4AF37]/10",
      risky: "text-[#EF4444] bg-[#EF4444]/10"
    };

    return (
      <div className="glass-card p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${tierColors[tier]}`}>
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-[#71717A]">Buyer Reputation</p>
              <p className="text-2xl text-[#EDEDED] font-bold">{score}</p>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${tierColors[tier]}`}>
            {tier}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#27272A]">
          <div className="text-center">
            <p className="text-lg text-[#EDEDED]">{reputation.reputation?.auctions_won || 0}</p>
            <p className="text-xs text-[#71717A]">Auctions Won</p>
          </div>
          <div className="text-center">
            <p className="text-lg text-[#EDEDED]">{reputation.reputation?.auctions_paid || 0}</p>
            <p className="text-xs text-[#71717A]">Paid On Time</p>
          </div>
          <div className="text-center">
            <p className="text-lg text-[#EF4444]">{reputation.reputation?.auctions_no_pay || 0}</p>
            <p className="text-xs text-[#71717A]">No-Pay</p>
          </div>
        </div>
      </div>
    );
  };

  const AuctionCard = ({ auction }) => (
    <Link
      to={`/auctions/${auction.auction_id}`}
      className="glass-card p-4 hover:border-[#D4AF37]/50 transition-colors block"
    >
      <div className="flex gap-4">
        <div className="w-20 h-20 rounded-lg bg-[#18181B] overflow-hidden flex-shrink-0">
          {auction.listing_photo ? (
            <img src={auction.listing_photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl">🐴</div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-[#EDEDED] truncate">{auction.listing_title}</h3>
            {getStatusBadge(auction)}
          </div>
          
          <div className="flex items-center gap-4 text-sm">
            <span className="text-[#D4AF37] font-medium">
              ${(auction.current_bid || auction.starting_bid).toLocaleString()}
            </span>
            <span className="text-[#71717A]">{auction.bid_count || 0} bids</span>
          </div>

          {activeTab === "bidding" && (
            <div className="mt-2">
              {auction.is_winning ? (
                <span className="inline-flex items-center gap-1 text-sm text-[#22C55E]">
                  <Trophy className="w-4 h-4" /> You're winning!
                </span>
              ) : auction.status === "active" ? (
                <span className="text-sm text-[#EF4444]">Outbid</span>
              ) : null}
            </div>
          )}

          {auction.status === "active" && (
            <div className="flex items-center gap-1 mt-2 text-sm text-[#A1A1AA]">
              <Clock className="w-4 h-4" />
              {formatTimeRemaining(auction.end_time)}
            </div>
          )}

          {auction.status === "ended" && auction.payment_deadline && (
            <div className="flex items-center gap-1 mt-2 text-sm text-[#F59E0B]">
              <AlertTriangle className="w-4 h-4" />
              Payment due: {new Date(auction.payment_deadline).toLocaleDateString()}
            </div>
          )}
        </div>

        <ChevronRight className="w-5 h-5 text-[#3F3F46] self-center" />
      </div>
    </Link>
  );

  return (
    <div className="pt-20 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl text-[#EDEDED] mb-2">My Auctions</h1>
          <p className="text-[#A1A1AA]">Manage your bids and auction listings</p>
        </div>

        {/* Reputation Card */}
        <ReputationCard />

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("bidding")}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              activeTab === "bidding"
                ? "bg-[#D4AF37] text-black"
                : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"
            }`}
          >
            My Bids
          </button>
          <button
            onClick={() => setActiveTab("selling")}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              activeTab === "selling"
                ? "bg-[#D4AF37] text-black"
                : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"
            }`}
          >
            My Auctions
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : auctions.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Gavel className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h3 className="text-xl text-[#EDEDED] mb-2">
              {activeTab === "bidding" ? "No Active Bids" : "No Auctions"}
            </h3>
            <p className="text-[#A1A1AA] mb-6">
              {activeTab === "bidding"
                ? "Browse live auctions and start bidding!"
                : "Start an auction from your breeder dashboard."}
            </p>
            <Link
              to={activeTab === "bidding" ? "/auctions" : "/dashboard/breeder"}
              className="btn-gold inline-flex items-center gap-2"
            >
              {activeTab === "bidding" ? (
                <>Browse Auctions <ChevronRight className="w-4 h-4" /></>
              ) : (
                <>Go to Dashboard <ChevronRight className="w-4 h-4" /></>
              )}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {auctions.map(auction => (
              <AuctionCard key={auction.auction_id} auction={auction} />
            ))}
          </div>
        )}

        {/* Quick Stats for Sellers */}
        {activeTab === "selling" && auctions.length > 0 && (
          <div className="glass-card p-6 mt-8">
            <h3 className="text-lg text-[#EDEDED] mb-4">Auction Summary</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl text-[#EDEDED]">
                  {auctions.filter(a => a.status === "active").length}
                </p>
                <p className="text-xs text-[#71717A]">Active</p>
              </div>
              <div className="text-center">
                <p className="text-2xl text-[#D4AF37]">
                  {auctions.filter(a => a.status === "ended").length}
                </p>
                <p className="text-xs text-[#71717A]">Pending Payment</p>
              </div>
              <div className="text-center">
                <p className="text-2xl text-[#22C55E]">
                  {auctions.filter(a => a.status === "sold").length}
                </p>
                <p className="text-xs text-[#71717A]">Sold</p>
              </div>
              <div className="text-center">
                <p className="text-2xl text-[#71717A]">
                  {auctions.filter(a => a.status === "no_sale").length}
                </p>
                <p className="text-xs text-[#71717A]">No Sale</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAuctions;
